import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	linkedSignal,
	output,
	resource,
	SecurityContext,
	signal,
	viewChild,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { DomSanitizer } from '@angular/platform-browser'
import {
	addAuditTrailMessageForEntityInstance,
	getAuditTrailEntriesForEntityInstance,
} from '../../bridges/frontend-audit-bridge.js'
import { FrontendUserBridge } from '../../bridges/frontend-user-bridge.js'
import { ChatMessage } from '@lg/shared'
import { EntityInstanceOnDb } from '@lg/shared'
import { MentionConfig } from 'angular-mentions'

@Component({
	selector: 'lowgile-chat',
	standalone: false,
	templateUrl: './chat.component.html',
	styleUrls: ['./chat.component.scss'],
})
export class ChatComponent {
	domSanitizer = inject(DomSanitizer)

	entityInstance = input<EntityInstanceOnDb | string>()
	auditedObj = computed(() => {
		const value = this.entityInstance()
		if (typeof value === 'string') {
			return { id: value }
		}
		return value
	})
	messages = input<ChatMessage[]>()
	isAllowSending = input(true)
	isSendAutomatically = input(true)

	send = output<string>()

	mentionConfig = computed(
		() =>
			({
				mentions: [
					{
						triggerChar: '@',
						labelKey: 'name',
						disableSearch: true,
						disableSort: true,
						allowSpace: true,
						returnTrigger: false,
						items: this.userSearchResults.value()?.matches ?? [],
						mentionSelect: (item) => `<b>${item.name}</b>`,
					},
				],
			}) as MentionConfig satisfies MentionConfig,
	)
	userSearchQuery = signal('')
	userSearchResults = resource({
		params: () => ({
			query: this.userSearchQuery(),
		}),
		loader: async ({ params }) =>
			params.query?.trim()
				? FrontendUserBridge.searchUser(params.query)
				: undefined,
	})

	entityInstanceMessages = resource({
		params: () => ({
			entityInstance: this.auditedObj(),
		}),
		loader: async ({ params }) => {
			if (!params.entityInstance) return []
			return getAuditTrailEntriesForEntityInstance(params.entityInstance)
		},
	})
	messageList = linkedSignal(() => {
		const messageList = (this.messages() ?? [])
			.concat(this.entityInstanceMessages.value() ?? [])
			.map((message) => ({
				message,
				isCurrentUser:
					message.userIdAndName.userId === FrontendUserBridge.currentUser?.id,
				actionType: message.type !== 'action' ? undefined : message.actionType,
			}))
		messageList.sort(
			(a, b) => a.message.timestamp.getTime() - b.message.timestamp.getTime(),
		)

		return messageList
	})

	newMessageEl = viewChild<ElementRef<HTMLDivElement>>('newMessage')

	async sendMessage() {
		const unsanitizedMessage =
			this.newMessageEl()?.nativeElement.innerHTML.trim() ?? ''
		const message = this.domSanitizer
			.sanitize(SecurityContext.HTML, unsanitizedMessage)
			?.toString()
		if (message) {
			if (this.isSendAutomatically()) {
				if (!this.auditedObj()) {
					throw new Error(
						'entityInstance is required when isSendAutomatically is true',
					)
				}
				await addAuditTrailMessageForEntityInstance(this.auditedObj(), message)
			}
			this.send.emit(message)
			this.newMessageEl()!.nativeElement.innerHTML = ''

			this.messageList.update((messages) => [
				...messages,
				{
					message: {
						id: 0,
						type: 'message',
						message,
						userIdAndName: FrontendUserBridge.currentUser?.toUserIdAndName(),
						timestamp: new Date(),
						attachments: [],
					},
					isCurrentUser: true,
					actionType: undefined,
				},
			])
		}
	}
}
